import type { Quality, Tier } from "@schema/catalog.ts";
import { useCatalog } from "@/lib/catalog/context.tsx";
import { useSettingsStore } from "@/state/settings.ts";
import styles from "./QualityBadge.module.css";

/** 0-100 internally, half-stars on screen — the scale readers rate on. */
const toStars = (score: number) => {
  const stars = Math.round((score / 20) * 2) / 2;
  return Number.isInteger(stars) ? String(stars) : stars.toFixed(1);
};

/**
 * The project's verdict on an entry.
 *
 * Quality is a separate axis from importance, and this badge exists to surface
 * the conflict rather than hide it: an entry can be required and mediocre.
 *
 * Shown as "★ 4.5" rather than five glyphs on purpose. A watched card already
 * carries a five-star control for the reader's OWN rating, and two five-star
 * rows would be indistinguishable at a glance — one compact, one interactive.
 *
 * Readers who don't want somebody else's opinion can turn it off entirely
 * (Settings → the rating toggle); this renders nothing when they have.
 */
export function QualityBadge({ quality, tier }: { quality: Quality; tier: Tier }) {
  const { rating } = useCatalog().project.vocabulary;
  const showRating = useSettingsStore((s) => s.showRating);
  // The project's flag outranks the reader's toggle: a deployment that does
  // not rate has no rating to show, whatever the stored setting says. Checked
  // here as well as in the header so a setting persisted from a project that
  // did rate cannot resurrect the badge.
  const rates = useCatalog().project.features.rating;

  if (!rates || !showRating) return null;

  // Nothing has been said about this yet — usually because it hasn't come out.
  // Rendering a 0 here would be a verdict, and the wrong one.
  if (quality.score === null) {
    return (
      <span
        className={`${styles.badge} ${styles.unrated}`}
        title={`No ${rating} yet. Nothing has been rated or reviewed for this.`}
      >
        <span className={styles.dot} data-band="none" />
        Unrated
      </span>
    );
  }
  const mandatory = tier === "essential" || tier === "important";
  const rough = quality.score < 65;
  const conflict = mandatory && rough;
  const gem = !mandatory && quality.score >= 85;
  const stars = toStars(quality.score);

  // A curated note beats the generated one. The heuristic below can only say
  // *that* tier and quality disagree; a curator who wrote it down says why.
  const generated = conflict
    ? `${tier === "essential" ? "Essential" : "Important"}, but mediocre. You still need it.`
    : gem
      ? "A great watch that Doomsday doesn't strictly require."
      : `${rating}: ${stars} out of 5.`;
  const note = quality.editorialNote ?? generated;

  return (
    <span
      className={`${styles.badge} ${conflict ? styles.conflict : ""}`}
      title={`${rating}: ${note}`}
    >
      <span aria-hidden="true" className={styles.star}>
        ★
      </span>
      {stars}
      <span className={styles.srOnly}>
        {rating}: {stars} out of 5
      </span>
      {conflict && <em className={styles.note}>still required</em>}
    </span>
  );
}
